import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { MdAccountTree, MdAutoAwesome, MdCheck, MdRefresh, MdSave } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../shared/layout/feedback/ErrorMessage';
import { AsyncSearchableSelect } from '../../../shared/layout/forms/AppSelect';
import {
    autoMapRetailerCategories,
    fetchRetailerCategoryMappings,
    searchGoogleProductCategories,
    updateRetailerCategoryMapping,
} from '../../../services/Http/Api';

const Toolbar = styled.div`display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;margin:16px 0;`;
const Actions = styled.div`display:flex;gap:8px;align-items:center;flex-wrap:wrap;`;
const Button = styled.button`
    display:inline-flex;align-items:center;justify-content:center;gap:7px;min-height:38px;padding:0 12px;
    border:1px solid ${p => p.$primary ? '#176b57' : '#cbd5d1'};border-radius:5px;
    background:${p => p.$primary ? '#176b57' : '#fff'};color:${p => p.$primary ? '#fff' : '#26332f'};
    font-weight:600;cursor:pointer;white-space:nowrap;
    &:disabled{opacity:.55;cursor:not-allowed;}
`;
const Filter = styled.select`min-height:38px;padding:0 30px 0 10px;border:1px solid #cbd5d1;border-radius:5px;background:#fff;color:#26332f;`;
const Summary = styled.div`display:flex;gap:20px;flex-wrap:wrap;padding:12px 0;color:#47534f;border-bottom:1px solid #e2e8e5;`;
const Count = styled.strong`color:#18231f;`;
const Notice = styled.p`margin:12px 0;color:#56645e;line-height:1.45;`;
const TableWrap = styled.div`overflow:auto;width:100%;`;
const Table = styled.table`
    width:100%;min-width:900px;border-collapse:collapse;font-size:14px;
    th{text-align:left;font-size:12px;font-weight:700;color:#68756f;background:#f6f8f7;padding:10px 12px;border-bottom:1px solid #dce3df;}
    td{padding:12px;border-bottom:1px solid #e7ece9;vertical-align:middle;}
`;
const Path = styled.div`font-weight:600;color:#1f2d28;overflow-wrap:anywhere;`;
const Meta = styled.div`margin-top:4px;font-size:12px;color:#738079;`;
const Status = styled.span`
    display:inline-flex;align-items:center;padding:4px 8px;border-radius:4px;font-size:12px;font-weight:600;
    color:${p => p.$done ? '#176b57' : '#805800'};background:${p => p.$done ? '#e6f4ee' : '#fff4d6'};
`;
const Empty = styled.div`padding:32px 12px;text-align:center;color:#68756f;`;
const ErrorText = styled.div`margin:12px 0;color:#a52828;`;

const optionFor = (category) => category ? {
    value: String(category.gpt_code),
    label: `${category.path} (${category.gpt_code})`,
} : null;

const RetailerCategoryMappings = () => {
    const { t } = useTranslation();
    const [payload, setPayload] = useState({ mappings: [], sources: [], summary: { total: 0, confirmed: 0, to_review: 0 } });
    const [sourceType, setSourceType] = useState(() => new URLSearchParams(window.location.search).get('source') || '');
    const [selected, setSelected] = useState({});
    const [saving, setSaving] = useState({});
    const [loading, setLoading] = useState(true);
    const [mapping, setMapping] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await fetchRetailerCategoryMappings(sourceType ? { source_type: sourceType } : {});
            setPayload(data);
            setSelected(Object.fromEntries((data.mappings || []).map(row => [row.id, optionFor(row.category)])));
        } catch (e) {
            setError(e.response?.data?.message || t('CategoryMappings.loadFailed'));
        } finally {
            setLoading(false);
        }
    }, [sourceType, t]);

    useEffect(() => { load(); }, [load]);

    const rows = useMemo(() => payload.mappings || [], [payload.mappings]);

    const save = async (row) => {
        const option = selected[row.id];
        if (!option?.value) return;
        setSaving(current => ({ ...current, [row.id]: true }));
        setError('');
        setNotice('');
        try {
            await updateRetailerCategoryMapping(row.id, option.value);
            setNotice(t('CategoryMappings.saved'));
            await load();
        } catch (e) {
            setError(e.response?.data?.message || t('CategoryMappings.saveFailed'));
        } finally {
            setSaving(current => ({ ...current, [row.id]: false }));
        }
    };

    const runAiMapping = async () => {
        setMapping(true);
        setError('');
        setNotice('');
        try {
            const result = await autoMapRetailerCategories(sourceType || undefined);
            setNotice(t('CategoryMappings.aiDone', result));
            await load();
        } catch (e) {
            setError(e.response?.data?.message || t('CategoryMappings.aiFailed'));
        } finally {
            setMapping(false);
        }
    };

    const loadOptions = async (input) => {
        if (!input || input.trim().length < 2) return [];
        try {
            const categories = await searchGoogleProductCategories(input.trim());
            return categories.map(category => optionFor(category));
        } catch (_) {
            return [];
        }
    };

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle icon={<MdAccountTree size={27} />}>{t('CategoryMappings.title')}</PageTitle>
            </PageHeader>
            <PageContent>
                <Notice>{t('CategoryMappings.description')}</Notice>
                <Toolbar>
                    <Actions>
                        <Filter value={sourceType} onChange={event => setSourceType(event.target.value)} aria-label={t('CategoryMappings.sourceFilter')}>
                            <option value="">{t('CategoryMappings.allSources')}</option>
                            {payload.sources.map(source => <option key={source} value={source}>{source}</option>)}
                        </Filter>
                        <Button type="button" onClick={load} disabled={loading} title={t('CategoryMappings.refresh')} aria-label={t('CategoryMappings.refresh')}>
                            <MdRefresh size={18} />
                        </Button>
                    </Actions>
                    <Button type="button" $primary onClick={runAiMapping} disabled={mapping || loading || (payload.summary?.ai_available || 0) === 0}>
                        <MdAutoAwesome size={18} />{mapping ? t('CategoryMappings.aiRunning') : t('CategoryMappings.aiAction')}
                    </Button>
                </Toolbar>
                <Summary>
                    <span>{t('CategoryMappings.total')}: <Count>{payload.summary?.total || 0}</Count></span>
                    <span>{t('CategoryMappings.confirmed')}: <Count>{payload.summary?.confirmed || 0}</Count></span>
                    <span>{t('CategoryMappings.review')}: <Count>{payload.summary?.to_review || 0}</Count></span>
                </Summary>
                {notice && <Notice role="status">{notice}</Notice>}
                {error && <ErrorText role="alert">{error}</ErrorText>}
                {loading ? <LoadingMessage /> : rows.length === 0 ? (
                    <Empty>{t('CategoryMappings.empty')}</Empty>
                ) : (
                    <TableWrap>
                        <Table>
                            <thead><tr>
                                <th>{t('CategoryMappings.source')}</th>
                                <th>{t('CategoryMappings.external')}</th>
                                <th>{t('CategoryMappings.target')}</th>
                                <th>{t('CategoryMappings.status')}</th>
                                <th>{t('CategoryMappings.action')}</th>
                            </tr></thead>
                            <tbody>{rows.map(row => (
                                <tr key={row.id}>
                                    <td>{row.source_type}</td>
                                    <td>
                                        <Path>{row.external_category_path || row.external_category_name || row.external_category_id}</Path>
                                        <Meta>{t('CategoryMappings.externalId')}: {row.external_category_id}</Meta>
                                    </td>
                                    <td>
                                        <AsyncSearchableSelect
                                            cacheOptions
                                            defaultOptions={false}
                                            loadOptions={loadOptions}
                                            value={selected[row.id] || null}
                                            onChange={(_, value, option) => setSelected(current => ({ ...current, [row.id]: option || (value ? { value, label: value } : null) }))}
                                            placeholder={t('CategoryMappings.searchTaxonomy')}
                                            aria-label={`${t('CategoryMappings.target')}: ${row.external_category_path}`}
                                        />
                                        {row.category && <Meta>GPT {row.category.gpt_code}{row.mapping_source === 'gemini' && row.mapping_confidence !== null ? ` · ${row.mapping_confidence}%` : ''}</Meta>}
                                    </td>
                                    <td><Status $done={row.mapping_status === 'confirmed'}>{t(`CategoryMappings.status_${row.mapping_status}`, { defaultValue: row.mapping_status })}</Status></td>
                                    <td>
                                        <Button type="button" onClick={() => save(row)} disabled={!selected[row.id]?.value || saving[row.id]}>
                                            {row.mapping_status === 'confirmed' ? <MdCheck size={17} /> : <MdSave size={17} />}
                                            {saving[row.id] ? t('CategoryMappings.saving') : t('CategoryMappings.save')}
                                        </Button>
                                    </td>
                                </tr>
                            ))}</tbody>
                        </Table>
                    </TableWrap>
                )}
            </PageContent>
        </PageContainer>
    );
};

export default RetailerCategoryMappings;
